import { useGetEnergyDataQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { buildTreeData } from '@/features/energyxplorer/utils/energyTreeFunctions';
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export function useSeuDetailedTimestampsSummary(
  accountId,
  selectedEnergyGroup,
  seu,
) {
  const timeRange = useSelector(selectTimeRange);
  const { data: energyData } = useGetEnergyDataQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: DateTime.now().startOf('day').toMillis(),
      end: DateTime.now().endOf('day').toMillis(),
      activePeriodFilter: undefined,
      activeDaysFilter: undefined,
      activeHolidaysFilter: undefined,
    },
    {
      skip: !selectedEnergyGroup?.energyGroupId || !accountId,
    },
  );

  const enhancedEnergyGroup = useMemo(() => {
    if (!selectedEnergyGroup || !energyData?.NodeEnergySummaries)
      return selectedEnergyGroup;

    const summaryMap = energyData?.NodeEnergySummaries.reduce(
      (acc, summary) => {
        acc[summary.Id] = summary;
        return acc;
      },
      {},
    );

    const enhancedNodes = selectedEnergyGroup?.energyTreeNodes?.map((node) => ({
      ...node,
      energySummary: summaryMap[node.energyTreeNodeId] || null,
    }));

    return {
      ...selectedEnergyGroup,
      energyTreeNodes: enhancedNodes,
    };
  }, [selectedEnergyGroup, energyData?.NodeEnergySummaries]);

  if (!enhancedEnergyGroup) return null;
  if (!seu) return null;

  const treeData = buildTreeData(enhancedEnergyGroup?.energyTreeNodes);
  const nodeTree = treeData
    .filter((n) => n.type === seu)
    .map((node) => {
      return {
        id: node.energyTreeNodeId,
        label: node.name,
        children: [],
        ...node,
        isOutOfBounds: isOutOfBounds(
          node.energySummary?.TimestampsSummary,
          timeRange,
        ),
      };
    });

  if (!nodeTree) return null;
  return nodeTree;
}
